import * as React from "react"
import { useSWRConfig } from "swr"
import { isSmithersEnabled } from "@/lib/feature-flags"

import {
  isSmithersBulkImportTerminal,
  useSmithersBulkImportJob,
} from "@/hooks/use-smithers-bulk-import-job"
import { toast } from "@/hooks/use-toast"
import {
  SMITHERS_BULK_IMPORT_JOB_ID_STORAGE_KEY,
  SMITHERS_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY,
  SmithersBulkImportJobContext,
} from "./smithers-bulk-import-job-context"
import type {
  SmithersBulkImportProgressJob,
  SmithersBulkImportUploadResult,
} from "@/types/smithers-bulk-import"
import type { SmithersBulkImportJobContextValue } from "./smithers-bulk-import-job-context"

function readStoredJobId(): string | null {
  if (typeof window === "undefined") return null
  return window.sessionStorage.getItem(SMITHERS_BULK_IMPORT_JOB_ID_STORAGE_KEY)
}

function isSmithersDeviceCacheKey(key: unknown): boolean {
  if (!Array.isArray(key) || key[0] !== "smithers") return false

  return key[1] === "devices"
}

function notifyJobComplete(job: SmithersBulkImportProgressJob): void {
  const description = `${job.succeeded} succeeded, ${job.failed} failed.`

  if (job.status === "COMPLETED") {
    toast({ title: "Smithers bulk import completed", description, variant: "success" })
    return
  }

  if (job.status === "COMPLETED_WITH_ERRORS") {
    toast({ title: "Smithers bulk import completed with errors", description, variant: "warning" })
    return
  }

  toast({ title: "Smithers bulk import failed", description, variant: "error" })
}

export function SmithersBulkImportJobProvider({ children }: React.PropsWithChildren) {
  const enabled = isSmithersEnabled()
  const [jobId, setJobId] = React.useState<string | null>(() => enabled ? readStoredJobId() : null)
  const activeJobId = enabled ? jobId : null
  const [provisionalJob, setProvisionalJob] = React.useState<SmithersBulkImportProgressJob>()
  const { data, error, isLoading } = useSmithersBulkImportJob(activeJobId)
  const { mutate } = useSWRConfig()
  const job = enabled ? (data?.jobId === jobId ? data : provisionalJob?.jobId === jobId ? provisionalJob : undefined) : undefined

  const startJob = React.useCallback((result: SmithersBulkImportUploadResult) => {
    if (!enabled) return
    window.sessionStorage.setItem(SMITHERS_BULK_IMPORT_JOB_ID_STORAGE_KEY, result.jobId)
    window.sessionStorage.removeItem(SMITHERS_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY)
    setProvisionalJob({
      jobId: result.jobId,
      status: result.status,
      totalRows: result.totalRows,
      processed: 0,
      succeeded: 0,
      skippedDuplicate: 0,
      failed: 0,
      errorSample: [],
    })
    setJobId(result.jobId)
  }, [enabled])

  const dismissJob = React.useCallback(() => {
    window.sessionStorage.removeItem(SMITHERS_BULK_IMPORT_JOB_ID_STORAGE_KEY)
    window.sessionStorage.removeItem(SMITHERS_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY)
    setProvisionalJob(undefined)
    setJobId(null)
  }, [])

  React.useEffect(() => {
    if (!job || !isSmithersBulkImportTerminal(job.status)) return
    if (
      window.sessionStorage.getItem(SMITHERS_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY) ===
      job.jobId
    ) {
      return
    }

    window.sessionStorage.setItem(SMITHERS_BULK_IMPORT_NOTIFIED_JOB_ID_STORAGE_KEY, job.jobId)
    notifyJobComplete(job)
    void mutate(isSmithersDeviceCacheKey)
  }, [job, mutate])

  const value = React.useMemo<SmithersBulkImportJobContextValue>(
    () => ({ jobId: activeJobId, job, error, isLoading, startJob, dismissJob }),
    [activeJobId, dismissJob, error, isLoading, job, startJob],
  )

  return (
    <SmithersBulkImportJobContext.Provider value={value}>
      {children}
    </SmithersBulkImportJobContext.Provider>
  )
}
